import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import {
  getSubscription,
  getCredits,
  createPortalSession,
  createCheckoutSession,
  getBillingConfig,
  SubscriptionInfo,
  CreditTransaction,
  CreditPack,
} from "../api/billing";
import { getAccessToken } from "../utils/auth";

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<SubscriptionInfo[]>([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate("/login");
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [subData, credData, configData] = await Promise.all([
        getSubscription(),
        getCredits(),
        getBillingConfig(),
      ]);
      setSubscriptions(subData.subscriptions);
      setCreditBalance(credData.balance);
      setTransactions(credData.transactions);
      setCreditPacks(configData.credit_packs);
    } catch (err) {
      setError("Failed to load billing data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      const url = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      setError("Failed to open subscription management. Please try again.");
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBuyCredits = async (priceId: string) => {
    setActionLoading(true);
    try {
      const url = await createCheckoutSession(priceId, "payment");
      window.location.href = url;
    } catch (err) {
      setError("Failed to start checkout. Please try again.");
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <span className="badge badge-success">Active</span>;
      case "past_due":
        return <span className="badge badge-warning">Past Due</span>;
      case "canceled":
        return <span className="badge badge-danger">Canceled</span>;
      default:
        return <span className="badge badge-info">{status}</span>;
    }
  };

  const getTxTypeBadge = (type: string) => {
    switch (type) {
      case "purchase":
        return <span className="badge badge-success">Purchase</span>;
      case "usage":
        return <span className="badge badge-info">Usage</span>;
      case "admin_grant":
        return <span className="badge badge-amber">Admin Grant</span>;
      case "refund":
        return <span className="badge badge-warning">Refund</span>;
      default:
        return <span className="badge badge-info">{type}</span>;
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-[#0a0b0f] flex items-center justify-center">
          <div className="text-zinc-400">Loading billing...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold text-zinc-100 mb-8">Billing</h1>

          {error && (
            <div className="alert alert-error mb-6">{error}</div>
          )}

          {/* Subscriptions Section */}
          <div className="card-dark rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-zinc-100">Subscriptions</h2>
              <div className="flex gap-3">
                {subscriptions.length > 0 && (
                  <button
                    onClick={handleManageSubscription}
                    disabled={actionLoading}
                    className={"btn-secondary px-4 py-2 rounded-xl text-sm" + (actionLoading ? " opacity-50" : "")}
                  >
                    Manage Subscription
                  </button>
                )}
                <button
                  onClick={() => navigate("/pricing")}
                  className="btn-primary px-4 py-2 rounded-xl text-sm"
                >
                  {subscriptions.length > 0 ? "Change Plan" : "Subscribe"}
                </button>
              </div>
            </div>

            {subscriptions.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-zinc-500 mb-4">No active subscriptions</div>
                <button
                  onClick={() => navigate("/pricing")}
                  className="btn-primary px-6 py-2.5 rounded-xl text-sm font-semibold"
                >
                  View Plans
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-4 rounded-xl bg-[#252830] border border-[#2d3139]">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-zinc-100">
                          {sub.product_type === "image_tier" ? "Photo Management" : "Claims Management"} — {sub.tier_id.charAt(0).toUpperCase() + sub.tier_id.slice(1)}
                        </span>
                        {getStatusBadge(sub.status)}
                      </div>
                      {sub.current_period_end && (
                        <div className="text-sm text-zinc-500">
                          {sub.cancel_at_period_end
                            ? "Cancels on " + formatDate(sub.current_period_end)
                            : "Renews on " + formatDate(sub.current_period_end)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AI Credits Section */}
          <div className="card-dark rounded-2xl p-6 mb-8">
            <h2 className="text-xl font-semibold text-zinc-100 mb-6">AI Credits</h2>

            <div className="flex items-center justify-between mb-6 p-4 rounded-xl bg-[#252830] border border-[#2d3139]">
              <div>
                <div className="text-sm text-zinc-500">Current Balance</div>
                <div className="text-3xl font-bold text-amber-400">{creditBalance.toFixed(2)}</div>
              </div>
              <div className="flex gap-2">
                {creditPacks.map((pack) => (
                  <button
                    key={pack.price_id}
                    onClick={() => handleBuyCredits(pack.price_id)}
                    disabled={actionLoading}
                    className={"btn-secondary px-3 py-2 rounded-lg text-xs" + (actionLoading ? " opacity-50" : "")}
                  >
                    Buy {pack.credits}
                  </button>
                ))}
              </div>
            </div>

            {/* Transaction History */}
            {transactions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  Recent Transactions
                </h3>
                <div className="overflow-x-auto">
                  <table className="table-dark">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Balance</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 20).map((tx) => (
                        <tr key={tx.id}>
                          <td className="text-sm">{formatDate(tx.created_at)}</td>
                          <td>{getTxTypeBadge(tx.transaction_type)}</td>
                          <td className={"text-sm font-medium " + (tx.amount >= 0 ? "text-emerald-400" : "text-rose-400")}>
                            {tx.amount >= 0 ? "+" : ""}{tx.amount.toFixed(2)}
                          </td>
                          <td className="text-sm text-zinc-400">{tx.balance_after.toFixed(2)}</td>
                          <td className="text-sm text-zinc-500">{tx.description || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingPage;
